import './style.modules.scss'

import {
  Notification,
  NotificationType,
  useSetBodyScroll
} from '@ampcore/adel-react'
import {
  EmailPayloadTemplate,
  EmailService,
  EmailServiceSendClientInquiryPayload
} from '@ampcore/client-side-services'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import xss, { whiteList } from 'xss'

// Input sanitization helpers for CWE-201 remediation (Flaw 685)
const sanitizeName = (value: string): string =>
  value.trim().replace(/[^a-zA-Z\s\-'.]/g, '')

const sanitizeEmail = (value: string): string =>
  value.trim().replace(/[<>]/g, '')

const removeControlChars = (value: string): string =>
  Array.from(value)
    .filter(char => {
      const code = char.codePointAt(0) ?? 0
      return code >= 32 && code !== 127
    })
    .join('')

const sanitizeComments = (value: string): string =>
  removeControlChars(value.trim())

export type EmailAdvisorModalProps = {
  isModalOpen: boolean
  analyticsName?: string
  psnAnalytics?: string
  onClose?: () => void
  advisorInfo: {
    name: string
    url?: string
  }
  payload: Omit<
    EmailServiceSendClientInquiryPayload,
    'fullName' | 'emailAddress' | 'comments'
  >
  disclosureText?: string
  testId?: string
}

export const EmailAdvisorModal = (props: EmailAdvisorModalProps) => {
  const {
    isModalOpen,
    analyticsName,
    psnAnalytics,
    onClose,
    disclosureText,
    testId,
    advisorInfo,
    payload
  } = props

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)
  const [isNameMissing, setIsNameMissing] = useState(false)
  const [isEmailMissing, setIsEmailMissing] = useState(false)
  const [isEmailSendError, setIsEmailSendError] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [validationStarted, setValidationStarted] = useState(false)
  const setBodyScrollLock = useSetBodyScroll(false)
  const modalTopRef = useRef<HTMLDivElement>(null)
  const inputNameRef = useRef<HTMLInputElement>(null)
  const inputEmailRef = useRef<HTMLInputElement>(null)
  const inputCommentsRef = useRef<HTMLTextAreaElement>(null)
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const atb = useMemo(
    () =>
      new AnalyticsTagBuilder()
        .setDefaultName('EmailAdvisorModal')
        .setAnalyticsName(analyticsName)
        .setPsnAnalytics(psnAnalytics),
    [analyticsName, psnAnalytics]
  )

  const emailService = new EmailService()

  const handleOnClose = () => {
    if (inputNameRef.current?.value) {
      inputNameRef.current.value = ''
    }
    if (inputEmailRef.current?.value) {
      inputEmailRef.current.value = ''
    }
    if (inputCommentsRef.current?.value) {
      inputCommentsRef.current.value = ''
    }
    setValidationStarted(false)
    setIsNameMissing(false)
    setIsEmailMissing(false)
    setIsSubmitDisabled(true)
    setIsEmailSent(false)
    setIsSubmitting(false)
    setIsEmailSendError(false)
    if (onClose) {
      onClose()
    }
  }

  const isFormValid = (): boolean => {
    const nameValue = inputNameRef.current?.value.trim() ?? ''
    const emailValue = inputEmailRef.current?.value.trim() ?? ''
    let hasError = false

    if (!nameValue) {
      setIsNameMissing(true)
      hasError = true
    } else {
      setIsNameMissing(false)
    }

    if (
      !emailValue ||
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(emailValue)
    ) {
      setIsEmailMissing(true)
      hasError = true
    } else {
      setIsEmailMissing(false)
    }

    return !hasError
  }

  const handleOnChange = () => {
    const nameValue = inputNameRef.current?.value.trim() ?? ''
    const emailValue = inputEmailRef.current?.value.trim() ?? ''
    const nameMissing = nameValue === ''
    const emailMissing =
      emailValue === '' ||
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(emailValue)
    setIsNameMissing(validationStarted ? nameMissing : false)
    setIsEmailMissing(validationStarted ? emailMissing : false)
    setIsSubmitDisabled(nameMissing || emailMissing)
  }

  const handleSubmit = (e?: React.MouseEvent<HTMLButtonElement>) => {
    setValidationStarted(true)

    if (!isFormValid()) {
      return
    }

    if (isSubmitDisabled) {
      if (submitButtonRef.current) {
        submitButtonRef.current.blur()
        if (e) e.preventDefault()
      }
      return
    }

    const sendEmail = async () => {
      const rawFullName = inputNameRef.current?.value ?? ''
      const rawEmail = inputEmailRef.current?.value ?? ''
      const rawComments = inputCommentsRef.current?.value ?? ''

      const emailPayload: EmailServiceSendClientInquiryPayload = {
        template: EmailPayloadTemplate.CLIENT_INQUIRY,
        recipient: payload.recipient,
        subject: payload.subject,
        greeting: payload.greeting,
        fullName: sanitizeName(rawFullName),
        emailAddress: sanitizeEmail(rawEmail),
        message: payload.message,
        comments: sanitizeComments(rawComments),
        overrideRecipient: payload.overrideRecipient
      }

      setIsSubmitting(true)

      const result = await emailService.send(emailPayload)
      if (result.success) {
        setIsEmailSent(true)
      } else {
        setIsEmailSendError(true)
        if (
          modalTopRef.current &&
          typeof modalTopRef.current.scrollIntoView === 'function'
        ) {
          modalTopRef.current?.scrollIntoView({ behavior: 'smooth' })
        }
      }

      setIsSubmitting(false)
    }

    sendEmail()
  }

  useEffect(() => {
    if (isModalOpen && inputNameRef.current) {
      inputNameRef.current.focus()
    }
  }, [isModalOpen])

  useEffect(() => {
    setBodyScrollLock(isModalOpen)
  }, [isModalOpen, setBodyScrollLock])

  if (!isModalOpen) return null

  if (isEmailSent) {
    return (
      <div className="EmailAdvisorModal Modal Modal--fullScreen">
        <dialog className="Modal-window">
          <div className="Modal-content Content">
            <div className="Modal-content-header--wrapper">
              <span className="Type-3 u-paddingRight16">
                Email your Ameriprise financial advisor
              </span>
              <button
                className="Button Button--icon Button--icon--neutral"
                onClick={handleOnClose}
                aria-label="Close this dialog"
                {...atb.dataAnalyticAttribute('close-button')}
              >
                <span aria-hidden="true">close</span>
              </button>
            </div>

            <div className="Email-success-notification">
              <Notification
                className="u-sizeConstrained u-marginTop20"
                type={NotificationType.CONFIRM}
                dismissable={false}
                message="Your message has been sent."
                testId={testId ? `${testId}-success-notification` : undefined}
              />
            </div>
          </div>
        </dialog>
      </div>
    )
  }

  return (
    <div className="EmailAdvisorModal Modal Modal--fullScreen">
      <dialog className="Modal-window">
        <div className="Modal-content Content">
          <div className="Modal-content-header--wrapper" ref={modalTopRef}>
            <span className="Type-3 u-paddingRight16">
              Email your Ameriprise financial advisor
            </span>
            <button
              className="Button Button--icon Button--icon--neutral"
              onClick={handleOnClose}
              aria-label="Close this dialog"
              {...atb.dataAnalyticAttribute('close-button')}
            >
              <span aria-hidden="true">close</span>
            </button>
          </div>
          {isEmailSendError && (
            <Notification
              className="u-sizeConstrained u-marginTop20 Email-error-notification u-marginBottom8"
              type={NotificationType.ALERT}
              dismissable={false}
              message="Something went wrong. Please try reloading your page or try again later."
              testId={testId ? `${testId}-error-notification` : undefined}
            />
          )}
          <div className="Form-fields">
            <div className="Form-group">
              <p className="Advisor-details">
                Your advisor:{' '}
                {advisorInfo.url ? (
                  <a href={xss(advisorInfo.url)} target="_blank">
                    {advisorInfo.name}
                  </a>
                ) : (
                  <span>{advisorInfo.name}</span>
                )}
              </p>
            </div>
            <div className="Form-group">
              <label htmlFor="input-name" className="Form-label">
                Your name
              </label>
              <input
                type="text"
                id="input-name"
                className={'Input' + (isNameMissing ? ' Input--error' : '')}
                name="input-name"
                ref={inputNameRef}
                onChange={handleOnChange}
                autoComplete="off"
                data-testid={testId ? `${testId}-name-input` : undefined}
              />
              {isNameMissing && (
                <p
                  className="Form-help Form-help--error"
                  data-testid={testId ? `${testId}-name-missing` : undefined}
                >
                  <span aria-hidden="true">warning</span>
                  <span>Please enter your name.</span>
                </p>
              )}
            </div>
            <div className="Form-group">
              <label htmlFor="input-email" className="Form-label">
                Your email address
              </label>
              <input
                type="text"
                id="input-email"
                className={'Input' + (isEmailMissing ? ' Input--error' : '')}
                name="input-name"
                ref={inputEmailRef}
                onChange={handleOnChange}
                autoComplete="off"
                data-testid={testId ? `${testId}-email-input` : undefined}
              />
              {isEmailMissing && (
                <p
                  className="Form-help Form-help--error"
                  data-testid={testId ? `${testId}-email-missing` : undefined}
                >
                  <span aria-hidden="true">warning</span>
                  <span>Please enter a valid email address.</span>
                </p>
              )}
            </div>
            <div className="Textarea-group">
              <label
                className="Input-label"
                htmlFor="message"
                data-testid={testId ? `${testId}-message` : undefined}
              >
                Message
              </label>
              <div
                className="EmailAdvisorModal-message Textarea u-padding16 u-bgColorStone"
                dangerouslySetInnerHTML={{
                  __html: xss(payload.message, {
                    whiteList: {
                      ...whiteList
                    }
                  })
                }}
              ></div>
            </div>
            <div className="Form-group">
              <label htmlFor="additional-comments" className="Form-label">
                Additional comments (optional)
              </label>
              <textarea
                id="additional-comments"
                className="Input"
                name="input-name"
                rows={2}
                maxLength={2000}
                data-testid={testId ? `${testId}-comments` : undefined}
                ref={inputCommentsRef}
              />
            </div>
            {disclosureText && (
              <h6
                className="u-textColorNeutral75"
                dangerouslySetInnerHTML={{ __html: xss(disclosureText) }}
                data-testid={testId ? `${testId}-disclosure-text` : undefined}
              ></h6>
            )}
          </div>
        </div>
        <footer className="Modal-footer">
          <div className="ButtonRow">
            <button
              name="Send email"
              onClick={handleSubmit}
              className={
                'EmailAdvisor-submit Button Button--primary' +
                (isSubmitDisabled ? ' disabled' : '')
              }
              data-tracking="Button"
              data-testid={testId ? `${testId}-submit-button` : undefined}
              ref={submitButtonRef}
              type="button"
              aria-disabled={isSubmitDisabled}
              {...atb.dataAnalyticAttribute('send-button')}
            >
              Send email
              {isSubmitting && (
                <div className="Loading Loading--small">
                  <div className="Loading-indicator"></div>
                  <div className="Loading-indicator"></div>
                  <div className="Loading-indicator"></div>
                  <div className="Loading-indicator"></div>
                </div>
              )}
            </button>
            <button
              className="Button Button--secondary Button--secondary--action-blue is-destructive"
              onClick={handleOnClose}
              {...atb.dataAnalyticAttribute('cancel-button')}
            >
              Cancel
            </button>
          </div>
        </footer>
      </dialog>
    </div>
  )
}
